import Modal from "../modal/Modal";
import { currencyFmt, ListSection } from "./BillingRateBuilderShared";

const TIER_HEADER_TO_TOTALS_KEY = {
    "EE Only": "eeOnly",
    "EES / EE+1": "eesEePlus1",
    EEC: "eec",
    "FAM / EE+2+": "famEePlus2",
};

const buildTieredMatrixRows = (breakdown) => {
    const { tierHeaders, tiered } = breakdown;
    const componentNames = new Set();
    tiered.forEach((rows) => rows.forEach((row) => componentNames.add(row.componentName)));

    return [...componentNames].sort().map((name) => [
        name,
        ...tierHeaders.map((tier, index) => {
            const hit = tiered[index]?.find((row) => row.componentName === name);
            return hit ? currencyFmt.format(hit.amount) : "—";
        }),
    ]);
};

const BillingRateBuilderTotalsBreakdownModal = ({
    isOpen,
    onClose,
    title,
    breakdown,
    expectedTotals,
}) => {
    if (!breakdown) return null;

    const {
        tierHeaders,
        base,
        baseSubtotal,
        tiered,
        pmpm,
        skipped,
        totals,
    } = breakdown;

    const hasTieredRows = tiered.some((rows) => rows.length > 0);
    const tieredSubtotals = tierHeaders.map((_, index) =>
        tiered[index].reduce((sum, row) => sum + row.amount, 0),
    );

    const finalTotalRows = tierHeaders.map((tier, index) => {
        const cells = [
            tier,
            currencyFmt.format(baseSubtotal),
            currencyFmt.format(tieredSubtotals[index]),
            currencyFmt.format(totals[index]),
        ];
        if (expectedTotals) {
            const key = TIER_HEADER_TO_TOTALS_KEY[tier];
            const expected = key ? expectedTotals[key] : null;
            cells.push(expected == null ? "—" : currencyFmt.format(Number(expected)));
        }
        return cells;
    });

    const finalHeaders = expectedTotals
        ? ["Tier", "Base", "Tiered", "Total", "XLSX expected"]
        : ["Tier", "Base", "Tiered", "Total"];

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className="billing-rate-builder__modal-content billing-rate-builder__totals-breakdown-modal">
                <p className="title billing-rate-builder__modal-title">{title}</p>
                <div className="billing-rate-builder__modal-content-body">
                    <ListSection
                        label="Non-tiered base (added to all tiers)"
                        headers={["Component", "Amount"]}
                        rows={[
                            ...base
                                .slice()
                                .sort((a, b) => a.componentName.localeCompare(b.componentName))
                                .map((row) => [row.componentName, currencyFmt.format(row.amount)]),
                            ["Base subtotal", currencyFmt.format(baseSubtotal)],
                        ]}
                    />

                    {hasTieredRows && (
                        <ListSection
                            label="Tiered additions"
                            headers={["Component", ...tierHeaders]}
                            rows={[
                                ...buildTieredMatrixRows(breakdown),
                                [
                                    "Tiered subtotal",
                                    ...tieredSubtotals.map((amount) => currencyFmt.format(amount)),
                                ],
                            ]}
                        />
                    )}

                    <ListSection
                        label="Final totals"
                        headers={finalHeaders}
                        rows={finalTotalRows}
                    />

                    {pmpm.length > 0 && (
                        <ListSection
                            label="PMPM (not included in tier totals)"
                            headers={["Component", "Amount"]}
                            rows={pmpm.map((row) => [row.componentName, currencyFmt.format(row.amount)])}
                        />
                    )}

                    {skipped.length > 0 && (
                        <p className="billing-rate-builder__muted billing-rate-builder__totals-breakdown-note">
                            Skipped {skipped.length} billing category row(s) with no resolvable rate.
                        </p>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default BillingRateBuilderTotalsBreakdownModal;
